<template>
  <div class="modal fade" id="statutoryDeductionManagementModal" tabindex="-1" aria-labelledby="statutoryDeductionManagementModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="statutoryDeductionManagementModalLabel">Statutory Deduction Management</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading statutory deductions...</p>
          </div>

          <div v-else>
            <!-- Controls section -->
            <div class="d-flex justify-content-between mb-3">
              <h6 class="mb-0 mt-2">Manage Statutory Deductions</h6>
              <button @click="showAddModal" class="btn btn-sm btn-primary">
                <i class="mdi mdi-plus me-1"></i> Add Statutory Deduction
              </button>
            </div>

            <!-- Statutory deductions table -->
            <div class="table-responsive">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Deduction Name</th>
                  <th>Calculation Method</th>
                  <th>Default Value</th>
                  <th>Income Limits</th>
                  <th>Max Contribution</th>
                  <th>Mandatory</th>
                  <th>Reference</th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="statutoryDeductions.length === 0">
                  <td colspan="8" class="text-center py-3">No statutory deductions found</td>
                </tr>
                <tr v-for="deduction in statutoryDeductions" :key="deduction.id">
                  <td>{{ deduction.deduction_name }}</td>
                  <td>{{ deduction.calculation_method }}</td>
                  <td>
                    <span v-if="deduction.calculation_method === 'Fixed'">
                      {{ formatCurrency(deduction.default_amount) }}
                    </span>
                    <span v-else-if="deduction.calculation_method === 'Percentage'">
                      {{ deduction.default_percentage }}%
                    </span>
                    <span v-else-if="deduction.calculation_method === 'Tiered'">
                      <button @click="viewTiers(deduction)" class="btn btn-sm btn-outline-info">
                        View Tiers
                      </button>
                    </span>
                    <span v-else>
                      Custom Calculation
                    </span>
                  </td>
                  <td>
                    <span v-if="deduction.min_income_limit || deduction.max_income_limit">
                      <small>
                        {{ deduction.min_income_limit ? formatCurrency(deduction.min_income_limit) : '0' }}
                        -
                        {{ deduction.max_income_limit ? formatCurrency(deduction.max_income_limit) : 'No Limit' }}
                      </small>
                    </span>
                    <span v-else class="text-muted">N/A</span>
                  </td>
                  <td>
                    <span v-if="deduction.max_contribution">
                      {{ formatCurrency(deduction.max_contribution) }}
                    </span>
                    <span v-else class="text-muted">N/A</span>
                  </td>
                  <td>
                    <span class="badge" :class="deduction.is_mandatory === '1' ? 'bg-danger' : 'bg-secondary'">
                      {{ deduction.is_mandatory === '1' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                  <td>{{ deduction.reference_number || 'N/A' }}</td>
                  <td class="text-center">
                    <button @click="editDeduction(deduction)" class="btn btn-sm btn-info me-1">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                    <button v-if="deduction.calculation_method === 'Tiered'" @click="manageTiers(deduction)" class="btn btn-sm btn-warning me-1" title="Manage Tiers">
                      <i class="mdi mdi-table-edit"></i>
                    </button>
                    <button @click="deleteDeduction(deduction.id)" class="btn btn-sm btn-danger">
                      <i class="mdi mdi-trash-can"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit Statutory Deduction Modal -->
  <div class="modal fade" id="statutoryDeductionFormModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Edit Statutory Deduction' : 'Add Statutory Deduction' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveDeduction">
            <div class="mb-3">
              <label for="deductionName" class="form-label">Deduction Name <span class="text-danger">*</span></label>
              <input
                  type="text"
                  class="form-control"
                  id="deductionName"
                  v-model="deductionForm.deduction_name"
                  required
                  placeholder="e.g. NSSF, NHIF, etc."
              >
            </div>

            <div class="mb-3">
              <label class="form-label">Calculation Method <span class="text-danger">*</span></label>
              <select
                  class="form-select"
                  v-model="deductionForm.calculation_method"
                  required
              >
                <option value="Fixed">Fixed Amount</option>
                <option value="Percentage">Percentage of Basic Salary</option>
                <option value="Tiered">Tiered Calculation</option>
                <option value="Custom">Custom Calculation</option>
              </select>
            </div>

            <div class="mb-3" v-if="deductionForm.calculation_method === 'Fixed'">
              <label for="defaultAmount" class="form-label">Default Amount <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="defaultAmount"
                    v-model="deductionForm.default_amount"
                    min="0"
                    step="0.01"
                    required
                >
              </div>
            </div>

            <div class="mb-3" v-if="deductionForm.calculation_method === 'Percentage'">
              <label for="defaultPercentage" class="form-label">Default Percentage <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    id="defaultPercentage"
                    v-model="deductionForm.default_percentage"
                    min="0"
                    max="100"
                    step="0.01"
                    required
                >
                <span class="input-group-text">%</span>
              </div>
            </div>

            <!-- NEW: Income Limits Section for Percentage Method -->
            <div v-if="deductionForm.calculation_method === 'Percentage'" class="border rounded p-3 mb-3 bg-light">
              <h6 class="mb-3">Income Limits & Caps (Optional - For NSSF, Housing Levy, etc.)</h6>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="minIncomeLimit" class="form-label">Minimum Income Limit (LEL)</label>
                  <div class="input-group">
                    <span class="input-group-text">KES</span>
                    <input
                        type="number"
                        class="form-control"
                        id="minIncomeLimit"
                        v-model="deductionForm.min_income_limit"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 0 or 8000"
                    >
                  </div>
                  <small class="text-muted">For NSSF: Lower Earnings Limit</small>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="maxIncomeLimit" class="form-label">Maximum Income Limit (UEL)</label>
                  <div class="input-group">
                    <span class="input-group-text">KES</span>
                    <input
                        type="number"
                        class="form-control"
                        id="maxIncomeLimit"
                        v-model="deductionForm.max_income_limit"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 72000"
                    >
                  </div>
                  <small class="text-muted">For NSSF: Upper Earnings Limit</small>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="maxContribution" class="form-label">Maximum Monthly Contribution</label>
                  <div class="input-group">
                    <span class="input-group-text">KES</span>
                    <input
                        type="number"
                        class="form-control"
                        id="maxContribution"
                        v-model="deductionForm.max_contribution"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 4320"
                    >
                  </div>
                  <small class="text-muted">Cap on monthly contribution amount</small>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="effectiveFrom" class="form-label">Effective From Date</label>
                  <input
                      type="date"
                      class="form-control"
                      id="effectiveFrom"
                      v-model="deductionForm.effective_from"
                  >
                  <small class="text-muted">When these limits became effective</small>
                </div>
              </div>
            </div>

            <div class="mb-3" v-if="deductionForm.calculation_method === 'Tiered'">
              <div class="alert alert-info">
                <i class="mdi mdi-information-outline me-2"></i>
                Tiered calculation details can be set up after creating the deduction.
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="isMandatory"
                    v-model="deductionForm.is_mandatory"
                >
                <label class="form-check-label" for="isMandatory">
                  Mandatory Deduction
                </label>
              </div>
              <small class="text-muted">If checked, this deduction will be automatically applied to all eligible employees</small>
            </div>

            <div class="mb-3">
              <label for="referenceNumber" class="form-label">Reference Number</label>
              <input
                  type="text"
                  class="form-control"
                  id="referenceNumber"
                  v-model="deductionForm.reference_number"
                  placeholder="e.g. Statutory regulation number"
              >
            </div>

            <div class="mb-3">
              <label for="description" class="form-label">Description</label>
              <textarea
                  class="form-control"
                  id="description"
                  v-model="deductionForm.description"
                  rows="3"
              ></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="saveDeduction"
              :disabled="!isFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Tiers Management Modal -->
  <div class="modal fade" id="tierManagementModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Manage Tiers for {{ currentDeductionName }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loadingTiers" class="text-center py-4">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading tiers...</p>
          </div>
          <div v-else>
            <div class="d-flex justify-content-between mb-3">
              <h6 class="mb-0 mt-2">Deduction Tiers</h6>
              <button @click="showAddTierForm" class="btn btn-sm btn-primary">
                <i class="mdi mdi-plus me-1"></i> Add Tier
              </button>
            </div>

            <!-- Tiers Table -->
            <div class="table-responsive">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Min Amount</th>
                  <th>Max Amount</th>
                  <th>Fixed Amount</th>
                  <th>Percentage</th>
                  <th>Effective Date</th>
                  <th>End Date</th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="deductionTiers.length === 0">
                  <td colspan="7" class="text-center py-3">No tiers defined yet</td>
                </tr>
                <tr v-for="tier in deductionTiers" :key="tier.id">
                  <td>{{ formatCurrency(tier.min_amount) }}</td>
                  <td>{{ tier.max_amount ? formatCurrency(tier.max_amount) : 'No Limit' }}</td>
                  <td>{{ tier.fixed_amount ? formatCurrency(tier.fixed_amount) : 'N/A' }}</td>
                  <td>{{ tier.percentage ? tier.percentage + '%' : 'N/A' }}</td>
                  <td>{{ formatDate(tier.effective_date) }}</td>
                  <td>{{ tier.end_date ? formatDate(tier.end_date) : 'No End Date' }}</td>
                  <td class="text-center">
                    <button @click="editTier(tier)" class="btn btn-sm btn-info me-1">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                    <button @click="deleteTier(tier.id)" class="btn btn-sm btn-danger">
                      <i class="mdi mdi-trash-can"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Add/Edit Tier Form -->
            <div v-if="showTierForm" class="border rounded p-3 mt-4">
              <h6 class="mb-3">{{ isEditingTier ? 'Edit Tier' : 'Add Tier' }}</h6>
              <form @submit.prevent="saveTier">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="tierMinAmount" class="form-label">Minimum Amount <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">KES</span>
                      <input
                          type="number"
                          class="form-control"
                          id="tierMinAmount"
                          v-model="tierForm.min_amount"
                          required
                          min="0"
                          step="0.01"
                          @change="validateTierRange"
                      >
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="tierMaxAmount" class="form-label">Maximum Amount</label>
                    <div class="input-group">
                      <span class="input-group-text">KES</span>
                      <input
                          type="number"
                          class="form-control"
                          id="tierMaxAmount"
                          v-model="tierForm.max_amount"
                          min="0"
                          step="0.01"
                          @change="validateTierRange"
                      >
                    </div>
                    <small class="text-muted">Leave blank for the highest tier</small>
                    <div v-if="tierRangeError" class="text-danger mt-1">{{ tierRangeError }}</div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="tierFixedAmount" class="form-label">Fixed Amount</label>
                    <div class="input-group">
                      <span class="input-group-text">KES</span>
                      <input
                          type="number"
                          class="form-control"
                          id="tierFixedAmount"
                          v-model="tierForm.fixed_amount"
                          min="0"
                          step="0.01"
                      >
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="tierPercentage" class="form-label">Percentage</label>
                    <div class="input-group">
                      <input
                          type="number"
                          class="form-control"
                          id="tierPercentage"
                          v-model="tierForm.percentage"
                          min="0"
                          max="100"
                          step="0.01"
                      >
                      <span class="input-group-text">%</span>
                    </div>
                    <div v-if="!tierForm.fixed_amount && !tierForm.percentage" class="text-danger mt-1">
                      Either fixed amount or percentage is required
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="tierEffectiveDate" class="form-label">Effective Date <span class="text-danger">*</span></label>
                    <input
                        type="date"
                        class="form-control"
                        id="tierEffectiveDate"
                        v-model="tierForm.effective_date"
                        required
                    >
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="tierEndDate" class="form-label">End Date</label>
                    <input
                        type="date"
                        class="form-control"
                        id="tierEndDate"
                        v-model="tierForm.end_date"
                    >
                    <small class="text-muted">Leave blank if there's no end date</small>
                  </div>
                </div>

                <div class="d-flex justify-content-end">
                  <button type="button" class="btn btn-secondary me-2" @click="cancelTierForm">
                    Cancel
                  </button>
                  <button
                      type="submit"
                      class="btn btn-primary"
                      :disabled="!isTierFormValid || submitLoading"
                  >
                    <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    {{ isEditingTier ? 'Update Tier' : 'Save Tier' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- View Tiers Modal -->
  <div class="modal fade" id="viewTiersModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ currentDeductionName }} Tiers</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loadingTiers" class="text-center py-4">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading tiers...</p>
          </div>
          <div v-else>
            <!-- Tiers Visualization -->
            <div class="tiers-visualization mb-4">
              <div v-for="(tier, index) in deductionTiers" :key="tier.id" class="tier-card">
                <div class="tier-header">
                  <h6 class="mb-0">Tier {{ index + 1 }}</h6>
                  <span class="badge bg-primary">{{ formatDate(tier.effective_date) }}</span>
                </div>
                <div class="tier-body">
                  <div class="tier-range">
                    <strong>Range:</strong> {{ formatCurrency(tier.min_amount) }}
                    <span v-if="tier.max_amount"> - {{ formatCurrency(tier.max_amount) }}</span>
                    <span v-else> and above</span>
                  </div>
                  <div class="tier-amount" v-if="tier.fixed_amount">
                    <strong>Fixed Amount:</strong> {{ formatCurrency(tier.fixed_amount) }}
                  </div>
                  <div class="tier-percentage" v-if="tier.percentage">
                    <strong>Percentage:</strong> {{ tier.percentage }}%
                  </div>
                </div>
              </div>
            </div>

            <!-- Tiers Table -->
            <div class="table-responsive">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Min Amount</th>
                  <th>Max Amount</th>
                  <th>Fixed Amount</th>
                  <th>Percentage</th>
                  <th>Effective Date</th>
                  <th>End Date</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="deductionTiers.length === 0">
                  <td colspan="6" class="text-center py-3">No tiers defined yet</td>
                </tr>
                <tr v-for="tier in deductionTiers" :key="tier.id">
                  <td>{{ formatCurrency(tier.min_amount) }}</td>
                  <td>{{ tier.max_amount ? formatCurrency(tier.max_amount) : 'No Limit' }}</td>
                  <td>{{ tier.fixed_amount ? formatCurrency(tier.fixed_amount) : 'N/A' }}</td>
                  <td>{{ tier.percentage ? tier.percentage + '%' : 'N/A' }}</td>
                  <td>{{ formatDate(tier.effective_date) }}</td>
                  <td>{{ tier.end_date ? formatDate(tier.end_date) : 'No End Date' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";

export default {
  name: 'StatutoryDeductionManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      loadingTiers: false,
      statutoryDeductions: [],
      deductionTiers: [],
      isEditing: false,
      currentId: null,
      currentDeductionName: '',
      deductionForm: {
        deduction_name: '',
        calculation_method: 'Fixed',
        default_amount: '',
        default_percentage: '',
        is_mandatory: true,
        reference_number: '',
        description: '',
        min_income_limit: '',
        max_income_limit: '',
        max_contribution: '',
        effective_from: ''
      },
      // Tier management
      showTierForm: false,
      isEditingTier: false,
      currentTierId: null,
      tierRangeError: '',
      tierForm: {
        min_amount: '',
        max_amount: '',
        fixed_amount: '',
        percentage: '',
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      },
      modal: null,
      formModal: null,
      tierModal: null,
      viewTiersModal: null
    };
  },
  computed: {
    isFormValid() {
      if (!this.deductionForm.deduction_name || !this.deductionForm.calculation_method) return false;

      if (this.deductionForm.calculation_method === 'Fixed' && !this.deductionForm.default_amount) {
        return false;
      }

      if (this.deductionForm.calculation_method === 'Percentage' && !this.deductionForm.default_percentage) {
        return false;
      }

      return true;
    },
    isTierFormValid() {
      if (!this.tierForm.min_amount || !this.tierForm.effective_date) return false;

      if (this.tierRangeError) return false;

      // Either fixed amount or percentage must be provided
      if (!this.tierForm.fixed_amount && !this.tierForm.percentage) return false;

      return true;
    }
  },
  mounted() {
    this.initModals();
  },
  methods: {
    initModals() {
      this.$nextTick(() => {
        // Initialize the main modal
        this.modal = new bootstrap.Modal(document.getElementById('statutoryDeductionManagementModal'));

        // Initialize form modal
        this.formModal = new bootstrap.Modal(document.getElementById('statutoryDeductionFormModal'));

        // Initialize tier management modal
        this.tierModal = new bootstrap.Modal(document.getElementById('tierManagementModal'));

        // Initialize view tiers modal
        this.viewTiersModal = new bootstrap.Modal(document.getElementById('viewTiersModal'));

        // Listen for modal hidden events to reset forms
        document.getElementById('statutoryDeductionFormModal').addEventListener('hidden.bs.modal', this.resetForm);
        document.getElementById('tierManagementModal').addEventListener('hidden.bs.modal', this.resetTierForm);
      });
    },
    showModal() {
      if (this.modal) {
        this.loadStatutoryDeductions();
        this.modal.show();
      } else {
        this.initModals();
        setTimeout(() => {
          if (this.modal) {
            this.loadStatutoryDeductions();
            this.modal.show();
          }
        }, 100);
      }
    },
    hideModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    showAddModal() {
      this.isEditing = false;
      this.currentId = null;
      this.resetForm();
      if (this.formModal) {
        this.formModal.show();
      }
    },
    showEditModal() {
      if (this.formModal) {
        this.formModal.show();
      }
    },
    hideFormModal() {
      if (this.formModal) {
        this.formModal.hide();
      }
    },
    resetForm() {
      this.deductionForm = {
        deduction_name: '',
        calculation_method: 'Fixed',
        default_amount: '',
        default_percentage: '',
        is_mandatory: true,
        reference_number: '',
        description: '',
        min_income_limit: '',
        max_income_limit: '',
        max_contribution: '',
        effective_from: ''
      };
      this.submitLoading = false;
    },
    // Tier Management
    showAddTierForm() {
      this.isEditingTier = false;
      this.currentTierId = null;
      this.resetTierForm();
      this.showTierForm = true;
    },
    cancelTierForm() {
      this.showTierForm = false;
      this.resetTierForm();
    },
    resetTierForm() {
      this.tierForm = {
        min_amount: '',
        max_amount: '',
        fixed_amount: '',
        percentage: '',
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      };
      this.tierRangeError = '';
      this.showTierForm = false;
      this.isEditingTier = false;
      this.currentTierId = null;
    },
    validateTierRange() {
      if (this.tierForm.min_amount !== '' && this.tierForm.max_amount !== '' &&
          parseFloat(this.tierForm.min_amount) >= parseFloat(this.tierForm.max_amount)) {
        this.tierRangeError = 'Maximum amount must be greater than minimum amount';
        return false;
      } else {
        this.tierRangeError = '';
        return true;
      }
    },
    // API Calls
    loadStatutoryDeductions() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getStatutoryDeductions");

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.statutoryDeductions = res.data.data.statutoryDeductions || [];
            } else {
              console.error("Error fetching statutory deductions:", res.data.message);
              alert(res.data.message || "Failed to load statutory deductions");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while loading statutory deductions");
          });
    },
    loadDeductionTiers(deductionId) {
      this.loadingTiers = true;

      const data = new FormData();
      data.append("function", "getStatutoryDeductionTiers");
      data.append("statutory_deduction_id", deductionId);

      execute(data, authUrl)
          .then((res) => {
            this.loadingTiers = false;

            if (res.data.success) {
              this.deductionTiers = res.data.data.tiers || [];
              // Sort tiers by min_amount to ensure visualization is correct
              this.deductionTiers.sort((a, b) => parseFloat(a.min_amount) - parseFloat(b.min_amount));
            } else {
              console.error("Error fetching deduction tiers:", res.data.message);
              alert(res.data.message || "Failed to load deduction tiers");
            }
          })
          .catch((error) => {
            this.loadingTiers = false;
            console.error("API Error:", error);
            alert("An error occurred while loading deduction tiers");
          });
    },
    editDeduction(deduction) {
      this.isEditing = true;
      this.currentId = deduction.id;

      // Copy deduction data to form
      this.deductionForm = {
        deduction_name: deduction.deduction_name,
        calculation_method: deduction.calculation_method,
        default_amount: deduction.default_amount || '',
        default_percentage: deduction.default_percentage || '',
        is_mandatory: deduction.is_mandatory === '1',
        reference_number: deduction.reference_number || '',
        description: deduction.description || '',
        min_income_limit: deduction.min_income_limit || '',
        max_income_limit: deduction.max_income_limit || '',
        max_contribution: deduction.max_contribution || '',
        effective_from: deduction.effective_from || ''
      };

      this.showEditModal();
    },
    deleteDeduction(id) {
      if (!confirm("Are you sure you want to delete this statutory deduction? This may affect payroll calculations.")) {
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "deleteStatutoryDeduction");
      data.append("id", id);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              alert(res.data.message || "Statutory deduction deleted successfully");
              this.loadStatutoryDeductions();
            } else {
              alert(res.data.message || "Failed to delete statutory deduction");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while deleting statutory deduction");
          });
    },
    saveDeduction() {
      if (!this.isFormValid) {
        alert("Please fill all required fields");
        return;
      }

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", this.isEditing ? "updateStatutoryDeduction" : "createStatutoryDeduction");

      if (this.isEditing) {
        data.append("id", this.currentId);
      }

      data.append("deduction_name", this.deductionForm.deduction_name);
      data.append("calculation_method", this.deductionForm.calculation_method);
      data.append("is_mandatory", this.deductionForm.is_mandatory ? 1 : 0);

      if (this.deductionForm.calculation_method === 'Fixed' && this.deductionForm.default_amount) {
        data.append("default_amount", this.deductionForm.default_amount);
      }

      if (this.deductionForm.calculation_method === 'Percentage' && this.deductionForm.default_percentage) {
        data.append("default_percentage", this.deductionForm.default_percentage);
      }

      if (this.deductionForm.reference_number) {
        data.append("reference_number", this.deductionForm.reference_number);
      }

      if (this.deductionForm.description) {
        data.append("description", this.deductionForm.description);
      }

      // NEW: Add the cap and limit fields
      if (this.deductionForm.min_income_limit) {
        data.append("min_income_limit", this.deductionForm.min_income_limit);
      }

      if (this.deductionForm.max_income_limit) {
        data.append("max_income_limit", this.deductionForm.max_income_limit);
      }

      if (this.deductionForm.max_contribution) {
        data.append("max_contribution", this.deductionForm.max_contribution);
      }

      if (this.deductionForm.effective_from) {
        data.append("effective_from", this.deductionForm.effective_from);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || (this.isEditing ? "Statutory deduction updated successfully" : "Statutory deduction created successfully"));
              this.hideFormModal();
              this.loadStatutoryDeductions();

              // If this is a new tiered deduction, ask if they want to add tiers now
              if (!this.isEditing && this.deductionForm.calculation_method === 'Tiered') {
                if (confirm("Do you want to add tiers for this deduction now?")) {
                  this.currentId = res.data.data.id;
                  this.currentDeductionName = this.deductionForm.deduction_name;
                  this.manageTiers({ id: this.currentId, deduction_name: this.currentDeductionName });
                }
              }
            } else {
              alert(res.data.message || "Failed to save statutory deduction");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while saving statutory deduction");
          });
    },
    // Tier Management
    manageTiers(deduction) {
      this.currentId = deduction.id;
      this.currentDeductionName = deduction.deduction_name;
      this.loadDeductionTiers(deduction.id);

      if (this.tierModal) {
        this.tierModal.show();
      }
    },
    viewTiers(deduction) {
      this.currentId = deduction.id;
      this.currentDeductionName = deduction.deduction_name;
      this.loadDeductionTiers(deduction.id);

      if (this.viewTiersModal) {
        this.viewTiersModal.show();
      }
    },
    editTier(tier) {
      this.isEditingTier = true;
      this.currentTierId = tier.id;

      // Copy tier data to form
      this.tierForm = {
        min_amount: tier.min_amount,
        max_amount: tier.max_amount || '',
        fixed_amount: tier.fixed_amount || '',
        percentage: tier.percentage || '',
        effective_date: tier.effective_date,
        end_date: tier.end_date || ''
      };

      this.showTierForm = true;
    },
    deleteTier(id) {
      if (!confirm("Are you sure you want to delete this tier?")) {
        return;
      }

      this.loadingTiers = true;

      const data = new FormData();
      data.append("function", "deleteStatutoryDeductionTier");
      data.append("id", id);

      execute(data, authUrl)
          .then((res) => {
            this.loadingTiers = false;

            if (res.data.success) {
              alert(res.data.message || "Tier deleted successfully");
              this.loadDeductionTiers(this.currentId);
            } else {
              alert(res.data.message || "Failed to delete tier");
            }
          })
          .catch((error) => {
            this.loadingTiers = false;
            console.error("API Error:", error);
            alert("An error occurred while deleting tier");
          });
    },
    saveTier() {
      if (!this.isTierFormValid) {
        alert("Please fill all required fields and fix any validation errors");
        return;
      }

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", this.isEditingTier ? "updateStatutoryDeductionTier" : "createStatutoryDeductionTier");

      if (this.isEditingTier) {
        data.append("id", this.currentTierId);
      }

      data.append("statutory_deduction_id", this.currentId);
      data.append("min_amount", this.tierForm.min_amount);
      data.append("effective_date", this.tierForm.effective_date);

      if (this.tierForm.max_amount) {
        data.append("max_amount", this.tierForm.max_amount);
      }

      if (this.tierForm.fixed_amount) {
        data.append("fixed_amount", this.tierForm.fixed_amount);
      }

      if (this.tierForm.percentage) {
        data.append("percentage", this.tierForm.percentage);
      }

      if (this.tierForm.end_date) {
        data.append("end_date", this.tierForm.end_date);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || (this.isEditingTier ? "Tier updated successfully" : "Tier created successfully"));
              this.showTierForm = false;
              this.resetTierForm();
              this.loadDeductionTiers(this.currentId);
            } else {
              alert(res.data.message || "Failed to save tier");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while saving tier");
          });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    }
  }
};
</script>

<style scoped>
.tiers-visualization {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.tier-card {
  flex: 1;
  min-width: 200px;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.tier-header {
  background: #f8f9fa;
  padding: 10px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tier-body {
  padding: 15px;
}

.tier-range, .tier-amount, .tier-percentage {
  margin-bottom: 8px;
}

.form-check {
  margin-bottom: 0.5rem;
}

.alert {
  margin-top: 1rem;
}
</style>